import { useState, useEffect } from "react";
import Lottie from "lottie-react";
import maleTrainingAnimation from "@/assets/lottie/male-training.json";
import femaleTrainingAnimation from "@/assets/lottie/female-training.json";
import { animate, motion, useMotionValue, useTransform } from "motion/react";
import { useEffectiveUser } from "@/hooks/useEffectiveUser";

export function UserHeader() {
  const { user, isLoading } = useEffectiveUser();

  const [showReminder, setShowReminder] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShowReminder(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  // animate volume total
  const total = (
    !user
      ? 0
      : user?.preferred_weight_unit === "lb"
      ? user?.volume_total_lb
      : user?.volume_total_kg
  ) as number;

  const count = useMotionValue(total * 0.9);
  const rounded = useTransform(() => Math.round(count.get()));

  useEffect(() => {
    if (total > 1) {
      const controls = animate(count, total, {
        duration: 0.5,
        type: "spring",
        visualDuration: 0.5,
        bounce: 0.1,
      });
      return () => controls.stop();
    }
  }, [total, count]);

  if (isLoading) {
    return (
      <div className="w-full h-24">
        <div className="flex justify-center items-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-bd-dark"></div>
        </div>
      </div>
    );
  }

  // New user: empty state
  if (user && user.volume_total_lb === null) {
    return (
      <div className="w-full h-24">
        {showReminder && (
          <motion.div
            initial={{ opacity: 0.5, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{
              duration: 0.4,
              scale: { type: "spring", visualDuration: 1, bounce: 0.3 },
            }}
          >
            <div className="w-full px-6 flex flex-col items-center ">
              <div className="container mx-auto w-[200px] pb-4 pl-2 flex justify-center gap-3 items-center text-tx-dark ">
                <Lottie
                  animationData={
                    user?.gender === "f"
                      ? femaleTrainingAnimation
                      : maleTrainingAnimation
                  }
                  loop
                  autoplay
                  style={{ height: 64, width: 64 }}
                />
                <span className="font-bold text-base font-playpen leading-5">
                  Your first set is awaiting
                </span>
              </div>

              <img
                src="/images/down-arrow.svg"
                alt="down arrow"
                className="h-8 w-8 animate-bounce-slow"
              />
            </div>
          </motion.div>
        )}
      </div>
    );
  }

  // User with tracked data
  return (
    <div className="w-full mb-6 px-6">
      <div className="w-full px-4 bg-white border border-bd-overlay rounded-full  ">
        <div className="flex justify-between items-center">
          <div className="flex flex-col p-2">
            <h1 className="flex justify-start items-center text-xl font-bold text-primary leading-6">
              <motion.pre>{rounded}</motion.pre>
              <span className="ml-2">{user?.preferred_weight_unit}</span>
            </h1>
            {/* Welcome Message */}
            <h2 className="text-sm font-normal text-tx-muted leading-5">
              Total Volume Tracked
            </h2>
          </div>

          {/* User Avatar */}
          <div className="h-10 w-10 rounded-full shadow-md overflow-hidden p-0 border border-gray-300 bg-white">
            <img
              src={user?.avatar_picture || "/images/default-avatar.svg"}
              alt={`${user?.first_name ?? ""} avatar`}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
